import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Space, Typography, Tooltip, message } from 'antd';
import { DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import { analyticsDefaults } from './analyticsDefaults';
import AgentScorecard from './AgentScorecard';
import ScorecardConfigModal from './ScorecardConfigModal';
import { calculateMetricValue, findMinValue, findMaxValue } from './scorecardUtils';
import ScorecardsExportModal from './ScorecardsExportModal';

const { Text } = Typography;

const modalState = {
    configModalVisible: false,
    exportModalVisible: false,
    setConfigModalVisible: (visible) => {
        modalState.configModalVisible = visible;
        if (modalState.renderModals) modalState.renderModals();
    },
    setExportModalVisible: (visible) => {
        modalState.exportModalVisible = visible;
        if (modalState.renderModals) modalState.renderModals();
    },
    renderModals: null
};

// Then in your component:
const ModalsContainer = React.memo(({
                                        scorecardConfig,
                                        onConfigChange,
                                        groupedRecords,
                                        sortedAgents,
                                        selectedAgents,
                                        metricMaxValues,
                                        availableFields
                                    }) => {
    const [, forceUpdate] = useState({});

    useEffect(() => {
        modalState.renderModals = () => forceUpdate({});
        return () => {
            modalState.renderModals = null;
        };
    }, []);

    return (
        <>
            <ScorecardConfigModal
                visible={modalState.configModalVisible}
                onClose={() => modalState.setConfigModalVisible(false)}
                config={scorecardConfig}
                onConfigChange={onConfigChange}
                availableFields={availableFields}
            />
            <ScorecardsExportModal
                visible={modalState.exportModalVisible}
                onClose={() => modalState.setExportModalVisible(false)}
                groupedRecords={groupedRecords}
                config={scorecardConfig}
                sortedAgents={sortedAgents}
                selectedAgents={selectedAgents}
                metricMaxValues={metricMaxValues}
            />
        </>
    );
});


const ScorecardsSection = ({
                               data,
                               config = analyticsDefaults.scorecards,
                               onConfigChange,
                               onFilterChange,
                                exportModalVisible
                           }) => {
    const [scorecardConfig, setScorecardConfig] = useState(config);
    const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [isExportModalVisible, setIsExportModalVisible] = useState(exportModalVisible || false);
    const ModalsContainer = React.memo(({
                                            scorecardConfig,
                                            onConfigChange,
                                            groupedRecords,
                                            sortedAgents,
                                            selectedAgents,
                                            metricMaxValues,
                                            availableFields
                                        }) => {
        const [, forceUpdate] = useState({});

        useEffect(() => {
            modalState.renderModals = () => forceUpdate({});
            return () => {
                modalState.renderModals = null;
            };
        }, []);

        return (
            <>
                <ScorecardConfigModal
                    visible={modalState.configModalVisible}
                    onClose={() => modalState.setConfigModalVisible(false)}
                    config={scorecardConfig}
                    onConfigChange={onConfigChange}
                    availableFields={availableFields}
                />
                <ScorecardsExportModal
                    visible={modalState.exportModalVisible}
                    onClose={() => modalState.setExportModalVisible(false)}
                    groupedRecords={groupedRecords}
                    config={scorecardConfig}
                    sortedAgents={sortedAgents}
                    selectedAgents={selectedAgents}
                    metricMaxValues={metricMaxValues}
                />
            </>
        );
    });

    useEffect(() => {
        setScorecardConfig(config);
    }, [config]);

    // Group records by the configured field
    const groupedRecords = useMemo(() => {
        return data.reduce((acc, record) => {
            const key = record[scorecardConfig.groupByField] || 'Unknown';
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(record);
            return acc;
        }, {});
    }, [data, scorecardConfig.groupByField]);

    // Calculate max values for each metric
    const metricMaxValues = useMemo(() => {
        const maxValues = {};
        const allMetrics = [
            ...(scorecardConfig.metrics?.wheels || []),
            ...(scorecardConfig.metrics?.bars || []),
        ];
        allMetrics.forEach((metric) => {
            if (!metric.useCustomMaxValue) {
                const values = Object.values(groupedRecords).map((groupRecords) =>
                    calculateMetricValue(groupRecords, metric)
                );
                maxValues[metric.id] = Math.max(...values.filter((v) => isFinite(v))) || 100;
            }
        });
        return maxValues;
    }, [groupedRecords, scorecardConfig.metrics]);

    // Sort agents based on sorting config
    // Replace the sortedAgents useMemo in ScorecardsSection with this improved version:

// Sort agents based on sorting config with special handling for recordCount
    // Sort scorecards based on sorting config
    const sortedAgents = useMemo(() => {
        // Get the sorting field and direction from config
        const sortField = scorecardConfig.sorting?.field;
        const sortDirection = scorecardConfig.sorting?.direction || 'desc';

        if (!sortField || Object.keys(groupedRecords).length === 0) {
            console.log("No sort field specified or no records, sorting alphabetically");
            return Object.keys(groupedRecords).sort();
        }

        // Special case: if sorting by groupByField, just sort alphabetically
        if (sortField === scorecardConfig.groupByField) {
            return Object.keys(groupedRecords).sort((a, b) => {
                return sortDirection === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
            });
        }

        // Special case: if sorting by recordCount, sort by the number of records
        if (sortField === 'recordCount') {
            return Object.keys(groupedRecords).sort((a, b) => {
                const countA = groupedRecords[a]?.length || 0;
                const countB = groupedRecords[b]?.length || 0;
                return sortDirection === 'asc' ? countA - countB : countB - countA;
            });
        }

        // Find the metric configuration for the sorting field
        const allMetrics = [
            ...(scorecardConfig.metrics?.wheels || []),
            ...(scorecardConfig.metrics?.bars || []),
            ...(scorecardConfig.metrics?.tags || []),
        ];

        if (process.env.NODE_ENV === 'development') {
           // console.log("Available metrics for sorting:", allMetrics.map(m => m.id));
        }

        const metricConfig = allMetrics.find(m => m.id === sortField);

        if (!metricConfig) {
            console.warn(`Sorting metric "${sortField}" not found in configured metrics, sorting alphabetically`);
            return Object.keys(groupedRecords).sort();
        }

        if (process.env.NODE_ENV === 'development') {
           // console.log(`Found metric config for ${sortField}:`, metricConfig);
        }

        // For each scorecard, calculate the value of the sorting metric
        const sortValues = {};

        Object.keys(groupedRecords).forEach(name => {
            const records = groupedRecords[name];
            sortValues[name] = calculateMetricValue(records, metricConfig);

            if (process.env.NODE_ENV === 'development') {
               // console.log(`${name}'s ${sortField} value: ${sortValues[name]}`);
            }
        });

        // Sort the names based on the calculated values
        const sorted = Object.keys(groupedRecords).sort((a, b) => {
            const valueA = sortValues[a] || 0;
            const valueB = sortValues[b] || 0;

            if (process.env.NODE_ENV === 'development') {
                //console.log(`Comparing ${a} (${valueA}) to ${b} (${valueB}) with direction ${sortDirection}`);
            }

            // Equal values should maintain alphabetical order for consistency
            if (valueA === valueB) {
                return a.localeCompare(b);
            }

            // Handle direction (asc or desc)
            if (sortDirection === 'desc') {
                return valueB - valueA;
            } else {
                return valueA - valueB;
            }
        });

        if (process.env.NODE_ENV === 'development') {
            //console.log(`Final sorted order: ${sorted}`);
        }

        return sorted;
    }, [groupedRecords, scorecardConfig.metrics, scorecardConfig.sorting, scorecardConfig.groupByField]);

    const handleConfigChange = (newConfig) => {
        setScorecardConfig(newConfig);
        if (onConfigChange) {
            onConfigChange(newConfig);
        }
    };

    // Handle scorecard selection toggling without immediate filtering
    const handleScorecardSelect = useCallback((agentName) => {
        setSelectedAgents(prev => {
            const isAlreadySelected = prev.includes(agentName);
            const newSelection = isAlreadySelected
                ? prev.filter(name => name !== agentName)
                : [...prev, agentName];

            return newSelection;
        });
    }, []);

// Handle applying filters based on current selections
    const handleApplySelectionFilter = () => {
        if (onFilterChange && selectedAgents.length > 0) {
            // First, ensure we clear any existing filter on this field
            onFilterChange(scorecardConfig.groupByField, []);

            // Then apply the new filter with a small delay to ensure they don't conflict
            setTimeout(() => {
                onFilterChange(scorecardConfig.groupByField, selectedAgents);
            }, 50);
        }
    };

// Handle clear all selections with proper filter removal
    const handleClearSelections = () => {
        setSelectedAgents([]);

        // Also clear any filters based on the grouping field
        if (onFilterChange) {
            onFilterChange(scorecardConfig.groupByField, []);
        }
    };

    const availableFields = data && data.length > 0
        ? Object.keys(data[0]).map((key) => ({
            id: key,
            title: prettifyFieldName(key),
            type: typeof data[0][key]
        })).sort((a, b) => a.title.localeCompare(b.title))
        : [];

    const openExportModal = () => {
        setIsExportModalVisible(true);
    };

    //console.log("Rendering ScorecardSection");
    return (
        <div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <Space>
                    {selectedAgents.length > 0 && (
                        <>
                            <Text style={{
                                color: '#6b7280',
                                fontSize: '14px',
                                marginRight: '8px'
                            }}>
                                {selectedAgents.length} {selectedAgents.length === 1 ? 'scorecard' : 'scorecards'} selected
                            </Text>
                            <Button
                                type="link"
                                onClick={handleApplySelectionFilter}
                                style={{ padding: '0', height: 'auto' }}
                            >
                                Filter Selected
                            </Button>
                            <Button
                                type="link"
                                onClick={handleClearSelections}
                                style={{ padding: '0', height: 'auto' }}
                            >
                                Clear Selection
                            </Button>
                        </>
                    )}
                </Space>
                <Space size={16}>
                    <Button
                        type="link"
                        onClick={() => modalState.setConfigModalVisible(true)}
                        style={{ padding: '0', height: 'auto' }}
                    >
                        Edit Scorecards
                    </Button>
                    <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => modalState.setExportModalVisible(true)}
                        disabled={sortedAgents.length === 0}
                        style={{ padding: '0', height: 'auto' }}
                    >
                        Export Data
                    </Button>
                </Space>
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '24px',
                    margin: '24px 0',
                }}
            >
                {sortedAgents.map((agent, index) => (
                    <AgentScorecard
                        key={agent}
                        name={agent}
                        records={groupedRecords[agent]}
                        allRecords={data}
                        config={scorecardConfig}
                        rank={index}
                        metricMaxValues={metricMaxValues}
                        isSelected={selectedAgents.includes(agent)}
                        onSelect={handleScorecardSelect}
                    />
                ))}
            </div>
            <ModalsContainer
                scorecardConfig={scorecardConfig}
                onConfigChange={handleConfigChange}
                groupedRecords={groupedRecords}
                sortedAgents={sortedAgents}
                selectedAgents={selectedAgents}
                metricMaxValues={metricMaxValues}
                availableFields={availableFields}
            />
        </div>
    );
};

const prettifyFieldName = (str) =>
    str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase());

export default React.memo(ScorecardsSection, (prevProps, nextProps) => {
    // Compare data by IDs, not by references
    const dataEqual = () => {
        if (!prevProps.data && !nextProps.data) return true;
        if (!prevProps.data || !nextProps.data) return false;
        if (prevProps.data.length !== nextProps.data.length) return false;

        // Just compare record IDs, not entire objects
        const prevIds = new Set(prevProps.data.map(record => record.id));
        return nextProps.data.every(record => prevIds.has(record.id));
    };


    const configsEqual = () => {
        return JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config);
    };

    // Skip function prop comparisons as they should be stable

    const areEqual = dataEqual() && configsEqual();

    // Debug logging
    if (!areEqual) {
        /*console.log('ScorecardsSection re-render reasons:', {
            dataChanged: !dataEqual(),
            configChanged: !configsEqual()
        });*/
    }

    return areEqual;
});
